import {
  CreateReportV2Request,
  EnterpriseReportBundleAssignmentListResponse,
  EnterpriseReportBundleAssignmentResponse,
  PublishReportV2Request,
  RecordResponse,
  ReportV2DeleteResponse,
  ReportV2FileUrlsResponse,
  ReportV2ListResponse,
  ReportV2Response,
  ReportV2StateResponse,
  ReportV2UploadResponse,
  ReportV2VersionResponse,
  StartableReportsResponse,
  UpdateRecordRequest,
  UpdateReportV2Request,
} from '@/types/reportV2Types';
import { FusaReply } from '@/types/replyTypes';
import { Client } from './client';

/** Reports filled in from inputs, and the records they render. */
export class ReportV2FrontendController {
  client: Client;

  constructor(client?: Client) {
    this.client = client || new Client();
  }

  list(): Promise<ReportV2ListResponse> {
    return this.client.read('/api/reports-v2');
  }

  /** the bundles this org may start a report from */
  startable(): Promise<StartableReportsResponse> {
    return this.client.read('/api/reports-v2/startable');
  }

  create(data: CreateReportV2Request): Promise<ReportV2Response> {
    return this.client.create('/api/reports-v2', data);
  }

  read(id: string): Promise<ReportV2StateResponse> {
    return this.client.read(`/api/reports-v2/${id}`);
  }

  update(id: string, data: UpdateReportV2Request): Promise<ReportV2Response> {
    return this.client.patch(`/api/reports-v2/${id}`, data);
  }

  /** says whether the record went with it */
  delete(id: string): Promise<ReportV2DeleteResponse> {
    return this.client.delete(`/api/reports-v2/${id}`);
  }

  publish(id: string, data: PublishReportV2Request = {}): Promise<ReportV2VersionResponse> {
    return this.client.create(`/api/reports-v2/${id}/versions`, data);
  }

  readVersion(id: string, versionId: string): Promise<ReportV2VersionResponse> {
    return this.client.read(`/api/reports-v2/${id}/versions/${versionId}`);
  }

  // ── records: what was entered ──

  /** `quiet` for an autosave, whose page says how it went; `signal` aborts it */
  updateRecord(recordId: string, data: UpdateRecordRequest, options?: { quiet?: boolean; signal?: AbortSignal }): Promise<RecordResponse> {
    return this.client.patch(`/api/records/${recordId}`, data, options);
  }

  /** a file record on the record and where to put its bytes */
  upload(recordId: string, fileName: string): Promise<ReportV2UploadResponse> {
    return this.client.create(`/api/records/${recordId}/files`, { fileName });
  }

  fileUrls(recordId: string, fileIds: string[]): Promise<ReportV2FileUrlsResponse> {
    return this.client.create(`/api/records/${recordId}/file-urls`, { fileIds });
  }

  // ── which enterprises may start reports from which bundles (staff) ──

  assignmentsForEnterprise(enterpriseId: string): Promise<EnterpriseReportBundleAssignmentListResponse> {
    return this.client.read(`/api/enterprise-report-bundle-assignments/all-for-enterprise/${enterpriseId}`);
  }

  assign(enterpriseId: string, reportBundleId: string): Promise<EnterpriseReportBundleAssignmentResponse> {
    return this.client.create('/api/enterprise-report-bundle-assignments', { enterpriseId, reportBundleId });
  }

  unassign(assignmentId: string): Promise<FusaReply> {
    return this.client.delete(`/api/enterprise-report-bundle-assignments/${assignmentId}`);
  }
}
